import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import SpinnerLoading from '../../../../components/SpinnerLoading';
import Button from '../../../../components/button';
import {usePaymentPrice} from '../../../shopping-basket/cash-payment/hooks/usePaymentPrice';
import {
  PaymentMethodValues,
  TransactionTypesChoices,
} from '../../../shopping-basket/cash-payment/utils/helpers';

interface IProps {
  transactionType: TransactionTypesChoices;
  paymentType: PaymentMethodValues | undefined;
  orderGuid?: string;
  storeOrderGuid?: string;
  isSubmitting?: boolean;
  onConfirm: (isCreditsEnough: boolean) => void;
  onUnavailable: () => void;
}

const CreditDetailsStep = ({
  transactionType,
  paymentType,
  orderGuid,
  storeOrderGuid,
  isSubmitting = false,
  onConfirm,
  onUnavailable,
}: IProps) => {
  const navigate = useNavigate();

  const {price, isSuccess, totalPrice, userBalance, isLoading} =
    usePaymentPrice({
      transactionType,
      paymentType,
      orderGuid,
      storeOrderGuid,
    });

  const isCreditsEnough =
    isSuccess &&
    userBalance !== undefined &&
    totalPrice !== undefined &&
    userBalance >= totalPrice;

  useEffect(() => {
    if (!isSuccess) return;

    if (userBalance === 0) {
      navigate('/my-credits');
    } else if (!totalPrice) {
      onUnavailable();
    }
  }, [isSuccess, userBalance, totalPrice, navigate, onUnavailable]);

  if (isSuccess && !totalPrice) return null;

  return (
    <div className="flex flex-col w-full gap-2">
      <p className="flex justify-center mt-2 text-sm font-medium text-gray-800">
        پرداخت اعتباری
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center">
          <SpinnerLoading size={45} />
        </div>
      ) : (
        <>
          {!isCreditsEnough && (
            <span className="px-3 mb-4 text-sm text-gray-500">
              مبلغ موارد خریداری شده بیشتر از اعتبار باقیمانده شماست. مبلغ مابین
              تفاوت را باید بصورت نقدی پرداخت فرمایید.
            </span>
          )}

          <div className="flex flex-col w-full px-3 space-y-3 text-sm font-medium text-gray-800">
            <PriceRow title="مبلغ سفارش" value={totalPrice ?? 0} />

            <Divider />

            <PriceRow title="میزان اعتبار شما" value={userBalance ?? 0} />

            {!isCreditsEnough && (
              <>
                <Divider />
                <PriceRow title="مبلغ نقد قابل پرداخت" value={price ?? 0} />
              </>
            )}
          </div>
        </>
      )}

      <div className="flex justify-end w-full px-3 mt-4">
        <Button
          className="w-full"
          onClick={() => onConfirm(isCreditsEnough)}
          disabled={isLoading || !isSuccess || isSubmitting}
          isLoading={isSubmitting}
        >
          پرداخت با اعتبار
        </Button>
      </div>
    </div>
  );
};

export default CreditDetailsStep;

const PriceRow = ({title, value}: {title: string; value: number}) => (
  <div className="flex justify-between text-gray-900 text-md">
    <span className="text-gray-700">{title}</span>
    <div className="flex items-center gap-1 font-medium ">
      <span className="text-gray text-md ">{value?.toLocaleString()}</span>
      <span className="text-sm">تومان</span>
    </div>
  </div>
);

const Divider = () => <div className="w-full h-[1px] bg-gray-200" />;
